import React, { useState, useEffect, useCallback } from 'react';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import './AdminManagement.css';

// French translations for permission display names
const permissionTranslations = {
  'View Users': 'Voir les utilisateurs',
  'Edit Users': 'Modifier les utilisateurs',
  'Delete Users': 'Supprimer les utilisateurs',
  'Approve User Deletion': 'Approuver suppression utilisateurs',
  'View Sales': 'Voir les ventes',
  'Export Sales': 'Exporter les ventes',
  'View Products': 'Voir les produits',
  'Edit Products': 'Modifier les produits',
  'Delete Products': 'Supprimer les produits',
  'View Groups': 'Voir les groupes',
  'Manage Groups': 'Gérer les groupes',
  'Delete Groups': 'Supprimer les groupes',
  'Approve Group Deletion': 'Approuver suppression groupes',
  'Manage Gestion Access': 'Gérer accès Gestion',
  'View Analytics': 'Voir les analytiques',
  'Export Analytics': 'Exporter les analytiques',
  'View Activities': 'Voir les activités',
  'View Real-time': 'Voir temps réel',
  'View Settings': 'Voir les paramètres',
  'Edit Settings': 'Modifier les paramètres',
  'View Admins': 'Voir les admins',
  'Manage Admins': 'Gérer les admins',
  'View Subscriptions': 'Voir les abonnements',
  'Edit Subscriptions': 'Modifier les abonnements',
  'View Approvals': 'Voir les approbations',
  'Manage Approvals': 'Gérer les approbations',
  'View Members': 'Voir les membres',
  'Manage Members': 'Gérer les membres',
  'View Audit': 'Voir l\'audit',
  'Manage Audit': 'Gérer l\'audit',
  'View Data Usage': 'Voir utilisation données'
};

// French translations for module names
const moduleTranslations = {
  'users': 'UTILISATEURS',
  'sales': 'VENTES',
  'products': 'PRODUITS',
  'groups': 'GROUPES',
  'analytics': 'ANALYTIQUES',
  'activities': 'ACTIVITÉS',
  'realtime': 'TEMPS RÉEL',
  'settings': 'PARAMÈTRES',
  'admins': 'ADMINISTRATEURS',
  'subscriptions': 'ABONNEMENTS',
  'approvals': 'APPROBATIONS',
  'members': 'MEMBRES',
  'audit': 'AUDIT',
  'data': 'DONNÉES'
};

// French translations for action types
const actionTranslations = {
  'login': 'Connexion',
  'create_admin': 'Création admin',
  'delete_admin': 'Suppression admin',
  'update_permissions': 'Modification permissions',
  'promote_super_admin': 'Promotion super admin',
  'demote_super_admin': 'Rétrogradation',
  'deactivate_admin': 'Désactivation',
  'reactivate_admin': 'Réactivation',
  'change_password': 'Changement mot de passe',
  'reset_password': 'Réinitialisation mot de passe',
  'recover_group': 'Récupération groupe',
  'delete_group': 'Suppression groupe',
  'revoke_gestion_access': 'Retrait accès Gestion',
  'grant_gestion_access': 'Octroi accès Gestion',
  'recover_user': 'Récupération utilisateur',
  'delete_user': 'Suppression utilisateur',
  'approve_deletion': 'Approbation suppression',
  'reject_deletion': 'Refus suppression',
  'approve_gestion': 'Approbation Gestion',
  'reject_gestion': 'Refus Gestion'
};

// Translate permission display name
const translatePermission = (displayName) => {
  return permissionTranslations[displayName] || displayName;
};

// Translate module name
const translateModule = (moduleName) => {
  return moduleTranslations[moduleName] || moduleName.toUpperCase();
};

// Translate action type
const translateAction = (action) => {
  return actionTranslations[action] || action.replace(/_/g, ' ');
};

// Format time ago in French (e.g., "5min", "2h", "3j")
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = Math.abs(now - date);
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) return 'quelques secondes';
  if (diffMin < 60) return `${diffMin}min`;
  if (diffHours < 24) {
    const remainingMin = diffMin % 60;
    return remainingMin > 0 ? `${diffHours}h ${remainingMin}min` : `${diffHours}h`;
  }
  if (diffDays < 30) {
    const remainingHours = diffHours % 24;
    return remainingHours > 0 ? `${diffDays}j ${remainingHours}h` : `${diffDays}j`;
  }
  return `${Math.floor(diffDays / 30)} mois`;
};

// Get initials from admin name (max 2 chars)
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const AdminManagement = () => {
  const { admin: currentAdmin, isOwner, isSuperAdmin, hasPermission } = useDashboardAuth();
  const [admins, setAdmins] = useState([]);
  const [permissions, setPermissions] = useState({ permissions: [], grouped: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showMyPasswordModal, setShowMyPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // New admin form
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    name: '',
    permissions: []
  });

  // Fetch admins
  const fetchAdmins = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/admins');
      setAdmins(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des administrateurs');
      console.error(err);
    }
  }, []);

  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/permissions');
      setPermissions(response.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }, []);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/audit?limit=50');
      setAuditLogs(response.data);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAdmins(), fetchPermissions()]);
      setLoading(false);
    };
    loadData();

    // Auto-refresh admins every 30s to keep online status live
    const refreshInterval = setInterval(() => {
      fetchAdmins();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [fetchAdmins, fetchPermissions]);

  // Add new admin
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post('/dashboard-admin/admins', newAdmin);
      setShowAddModal(false);
      setNewAdmin({ email: '', password: '', name: '', permissions: [] });
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    }
  };

  // Update permissions
  const handleUpdatePermissions = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/dashboard-admin/admins/${selectedAdmin.id}/permissions`, {
        permissions: selectedAdmin.selectedPermissions
      });
      setShowEditModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  // Promote to super admin
  const handlePromote = async (adminId) => {
    if (!window.confirm('Voulez-vous vraiment promouvoir cet administrateur en Super Admin?')) return;
    try {
      await api.put(`/dashboard-admin/admins/${adminId}/promote`);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la promotion');
    }
  };

  // Demote from super admin
  const handleDemote = async (adminId) => {
    if (!window.confirm('Voulez-vous vraiment rétrograder ce Super Admin?')) return;
    try {
      await api.put(`/dashboard-admin/admins/${adminId}/demote`, { permissions: [] });
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la rétrogradation');
    }
  };

  // Deactivate admin
  const handleDeactivate = async (adminId) => {
    if (!window.confirm('Voulez-vous vraiment désactiver cet administrateur?')) return;
    try {
      await api.put(`/dashboard-admin/admins/${adminId}/deactivate`);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la désactivation');
    }
  };

  // Reactivate admin
  const handleReactivate = async (adminId) => {
    try {
      await api.put(`/dashboard-admin/admins/${adminId}/reactivate`);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réactivation');
    }
  };

  // Delete admin
  const handleDelete = async (adminId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer définitivement cet administrateur?')) return;
    try {
      await api.delete(`/dashboard-admin/admins/${adminId}`);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Reset password for other admin
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newPassword = e.target.newPassword.value;
    try {
      await api.put(`/dashboard-admin/admins/${selectedAdmin.id}/reset-password`, { newPassword });
      setShowPasswordModal(false);
      setSelectedAdmin(null);
      setSuccessMessage('Mot de passe réinitialisé avec succès');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation');
    }
  };

  // Change my own password
  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    const currentPassword = e.target.currentPassword.value;
    const newPassword = e.target.newPassword.value;
    const confirmPassword = e.target.confirmPassword.value;

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await api.put('/dashboard-admin/me/password', { currentPassword, newPassword });
      setShowMyPasswordModal(false);
      setSuccessMessage('Votre mot de passe a été modifié avec succès');
      setTimeout(() => setSuccessMessage(''), 3000);
      e.target.reset();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du changement de mot de passe');
    }
  };

  // Open edit modal
  const openEditModal = (admin) => {
    setSelectedAdmin({
      ...admin,
      selectedPermissions: admin.permissions === 'all' 
        ? permissions.permissions.map(p => p.name)
        : admin.permissions.map(p => p.name || p)
    });
    setShowEditModal(true);
  };

  // Toggle permission in form
  const togglePermission = (permName, isNewAdmin = false) => {
    if (isNewAdmin) {
      setNewAdmin(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permName)
          ? prev.permissions.filter(p => p !== permName)
          : [...prev.permissions, permName]
      }));
    } else {
      setSelectedAdmin(prev => ({
        ...prev,
        selectedPermissions: prev.selectedPermissions.includes(permName)
          ? prev.selectedPermissions.filter(p => p !== permName)
          : [...prev.selectedPermissions, permName]
      }));
    }
  };

  // Get role badge
  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return <span className="role-badge owner">👑 Propriétaire</span>;
      case 'super_admin':
        return <span className="role-badge super-admin">⭐ Super Admin</span>;
      default:
        return <span className="role-badge admin">👤 Admin</span>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="admin-management loading">Chargement...</div>;
  }

  if (!hasPermission('admins.view')) {
    return (
      <div className="admin-management access-denied">
        <h2>Accès refusé</h2>
        <p>Vous n'avez pas la permission de voir cette page.</p>
      </div>
    );
  }

  return (
    <div className="admin-management">
      <div className="admin-header">
        <h1>👥 Gestion des Administrateurs</h1>
        <div className="header-actions">
          <button 
            className="btn-password-change"
            onClick={() => setShowMyPasswordModal(true)}
          >
            🔐 Changer mon mot de passe
          </button>
          {hasPermission('admins.view') && (
            <button 
              className="btn-secondary"
              onClick={() => {
                fetchAuditLogs();
                setShowAuditLogs(true);
              }}
            >
              📋 Historique
            </button>
          )}
          {hasPermission('admins.manage') && (
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              + Ajouter un Admin
            </button>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="success-banner">
          {successMessage}
          <button onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="admins-grid">
        {admins.map(admin => (
          <div key={admin.id} className={`admin-card ${!admin.is_active ? 'inactive' : ''}`}>
            <div className="admin-card-header">
              <div className={`admin-avatar ${admin.is_online ? 'online' : 'offline'}`}>
                {getInitials(admin.name)}
                {admin.is_online && <span className="avatar-online-dot"></span>}
              </div>
              <div className="admin-info">
                <h3>{admin.name}</h3>
                <p className="email">{admin.email}</p>
                <div className="admin-badges-row">
                  {getRoleBadge(admin.role)}
                  {!admin.is_active && <span className="status-badge inactive">Désactivé</span>}
                  {admin.is_active && (
                    admin.is_online ? (
                      <span className="online-status online">
                        <span className="online-dot"></span>
                        EN LIGNE
                        {admin.last_seen && <small> · Depuis {formatTimeAgo(admin.last_seen)}</small>}
                      </span>
                    ) : admin.last_seen ? (
                      <span className="online-status offline">
                        <span className="offline-dot"></span>
                        HORS LIGNE
                        <small> · Il y'a {formatTimeAgo(admin.last_seen)}</small>
                      </span>
                    ) : (
                      <span className="online-status offline">
                        <span className="offline-dot"></span>
                        HORS LIGNE
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="admin-card-body">
              <div className="admin-meta">
                <p><strong>Dernière connexion:</strong> {formatDate(admin.last_login)}</p>
                <p><strong>Créé le:</strong> {formatDate(admin.created_at)}</p>
                {admin.created_by_name && (
                  <p><strong>Créé par:</strong> {admin.created_by_name}</p>
                )}
                {admin.role === 'super_admin' && admin.promoted_by_name && (
                  <p><strong>Promu par:</strong> {admin.promoted_by_name}</p>
                )}
              </div>

              {admin.role === 'admin' && admin.permissions !== 'all' && (
                <div className="admin-permissions">
                  <strong>Permissions:</strong>
                  <div className="permission-tags">
                    {admin.permissions.length === 0 ? (
                      <span className="no-perms">Aucune permission</span>
                    ) : (
                      admin.permissions.map((p, idx) => (
                        <span key={idx} className="perm-tag">
                          {translatePermission(p.display_name || p)}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {admin.id !== currentAdmin.id && admin.role !== 'owner' && hasPermission('admins.manage') && (
              <div className="admin-card-actions">
                {admin.role === 'admin' && (
                  <button className="btn-sm btn-edit" onClick={() => openEditModal(admin)}>
                    Permissions
                  </button>
                )}
                
                {isOwner() && admin.role === 'admin' && (
                  <button className="btn-sm btn-promote" onClick={() => handlePromote(admin.id)}>
                    ⬆️ Super Admin
                  </button>
                )}
                
                {isOwner() && admin.role === 'super_admin' && (
                  <button className="btn-sm btn-demote" onClick={() => handleDemote(admin.id)}>
                    ⬇️ Rétrograder
                  </button>
                )}

                {isSuperAdmin() && (
                  <button 
                    className="btn-sm btn-password" 
                    onClick={() => {
                      setSelectedAdmin(admin);
                      setShowPasswordModal(true);
                    }}
                  >
                    🔑 Mot de passe
                  </button>
                )}

                {admin.is_active ? (
                  <button className="btn-sm btn-deactivate" onClick={() => handleDeactivate(admin.id)}>
                    Désactiver
                  </button>
                ) : (
                  <button className="btn-sm btn-activate" onClick={() => handleReactivate(admin.id)}>
                    Activer
                  </button>
                )}

                {(isOwner() || (isSuperAdmin() && admin.role !== 'super_admin')) && (
                  <button className="btn-sm btn-delete" onClick={() => handleDelete(admin.id)}>
                    🗑️ Supprimer
                  </button>
                )}
              </div>
            )}

            {admin.id === currentAdmin.id && (
              <div className="admin-card-actions">
                <span className="current-user-badge">C'est vous</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter un Administrateur</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddAdmin}>
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={newAdmin.name}
                  onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mot de passe</label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group permissions-group">
                <label>Permissions</label>
                <div className="permissions-grid">
                  {Object.entries(permissions.grouped).map(([module, perms]) => (
                    <div key={module} className="permission-module">
                      <h4>{translateModule(module)}</h4>
                      {perms.map(perm => (
                        <label key={perm.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={newAdmin.permissions.includes(perm.name)}
                            onChange={() => togglePermission(perm.name, true)}
                          />
                          {translatePermission(perm.display_name)}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Permissions Modal */}
      {showEditModal && selectedAdmin && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier les Permissions</h2>
              <p className="modal-subtitle">{selectedAdmin.name} ({selectedAdmin.email})</p>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdatePermissions}>
              <div className="permissions-grid">
                {Object.entries(permissions.grouped).map(([module, perms]) => (
                  <div key={module} className="permission-module">
                    <h4>{translateModule(module)}</h4>
                    {perms.map(perm => (
                      <label key={perm.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedAdmin.selectedPermissions.includes(perm.name)}
                          onChange={() => togglePermission(perm.name, false)}
                        />
                        {translatePermission(perm.display_name)}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal (for other admins) */}
      {showPasswordModal && selectedAdmin && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Réinitialiser le Mot de Passe</h2>
              <p className="modal-subtitle">{selectedAdmin.name}</p>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  name="newPassword"
                  required
                  minLength={6}
                  placeholder="Minimum 6 caractères"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Réinitialiser
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change My Password Modal */}
      {showMyPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowMyPasswordModal(false)}>
          <div className="modal modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔐 Changer Mon Mot de Passe</h2>
              <button className="close-btn" onClick={() => setShowMyPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handleChangeMyPassword}>
              <div className="form-group">
                <label>Mot de passe actuel</label>
                <input
                  type="password"
                  name="currentPassword"
                  required
                  placeholder="Votre mot de passe actuel"
                />
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  name="newPassword"
                  required
                  minLength={6}
                  placeholder="Minimum 6 caractères"
                />
              </div>
              <div className="form-group">
                <label>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  name="confirmPassword"
                  required
                  minLength={6}
                  placeholder="Répétez le nouveau mot de passe"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowMyPasswordModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Changer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Audit Logs Modal */}
      {showAuditLogs && (
        <div className="modal-overlay" onClick={() => setShowAuditLogs(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Historique des Actions</h2>
              <button className="close-btn" onClick={() => setShowAuditLogs(false)}>×</button>
            </div>
            <div className="audit-logs">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Cible</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td>{formatDate(log.created_at)}</td>
                      <td>{log.admin_name}</td>
                      <td>
                        <span className={`action-badge action-${log.action}`}>
                          {translateAction(log.action)}
                        </span>
                      </td>
                      <td>{log.target_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
